<script setup>
import { ref, computed, onMounted } from 'vue'
import { useNotificationStore } from '@/stores/notification'
import { useCampanhaMarketingStore } from '@/stores/campanhaMarketing'

const notificationStore = useNotificationStore()
const campanhaStore = useCampanhaMarketingStore()

// Dados das campanhas SMS
const campanhas = ref([])
const loading = ref(false)
const searchQuery = ref('')
const selectedStatus = ref('')
const expandedRow = ref(null)
const currentPage = ref(1)
const perPage = ref(15)
const statisticsCache = ref({}) // Cache para estatísticas de cliques

// Carregar campanhas ao montar o componente
onMounted(async () => {
  await carregarCampanhas()
})

// Função para carregar campanhas da API
const carregarCampanhas = async () => {
  loading.value = true
  try {
    const resultado = await campanhaStore.listarCampanhasSMS(currentPage.value, perPage.value)
    
    // Mapear dados da API para o formato do componente
    campanhas.value = (resultado.data || []).map(campanha => ({
      id: campanha.id,
      nome_campanha: campanha.nome_campanha,
      mensagem: campanha.mensagem,
      data_envio: campanha.data_envio || campanha.created_at,
      status: campanha.status,
      total_clientes: campanha.total_clientes,
      clientes_sucesso: campanha.clientes_enviado,
      clientes_erro: campanha.clientes_erro,
      clientes_clicaram: campanha.clientes_clicaram || 0,
      filtros: {
        estados: campanha.estados ? JSON.parse(campanha.estados || '[]') : [],
        cidade: campanha.cidade,
        data_inicio: campanha.data_inicio,
        data_fim: campanha.data_fim,
        dias_vencimento: campanha.dias_vencimento,
        dias_vencimento_filtro: campanha.dias_vencimento_filtro,
        telefone: campanha.telefone,
        nunca_comprou: campanha.nunca_comprou
      }
    }))
    
    // Pré-carregar estatísticas de cliques para campanhas enviadas
    for (const campanha of campanhas.value) {
      if (campanha.status === 'enviado' && !statisticsCache.value[campanha.id]) {
        carregarEstatisticasCliques(campanha.id)
      }
    }
  } catch (error) {
    notificationStore.showNotification('Erro ao carregar campanhas: ' + error.message, 'error')
  } finally {
    loading.value = false
  }
}

// Função para carregar estatísticas de cliques de uma campanha
const carregarEstatisticasCliques = async (campanhaId) => {
  try {
    const stats = await campanhaStore.obterEstatisticasCliques(campanhaId)
    statisticsCache.value[campanhaId] = stats.data || {}
  } catch (error) {
    console.error('Erro ao carregar estatísticas:', error)
  }
}

// Filtrar campanhas
const filteredCampanhas = computed(() => {
  return campanhas.value.filter(campanha => {
    const matchSearch = campanha.nome_campanha.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                       campanha.mensagem.toLowerCase().includes(searchQuery.value.toLowerCase())
    const matchStatus = !selectedStatus.value || campanha.status === selectedStatus.value
    return matchSearch && matchStatus
  })
})

// Calcular taxa de sucesso
const taxaSucesso = (campanha) => {
  if (campanha.total_clientes === 0) return 0
  return ((campanha.clientes_sucesso / campanha.total_clientes) * 100).toFixed(1)
}

// Obter taxa de clique
const obterTaxaClique = (campanhaId, campanha) => {
  const stats = statisticsCache.value[campanhaId]
  if (!stats) return '0'
  
  const totalEnviados = campanha.clientes_sucesso
  if (totalEnviados === 0) return '0'
  
  return ((campanha.clientes_clicaram / totalEnviados) * 100).toFixed(1)
}

// Formatar data
const formatarData = (data) => {
  return new Date(data).toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Construir descrição dos filtros
const descricaoFiltros = (campanha) => {
  const filtros = []
  
  if (campanha.filtros.estados && campanha.filtros.estados.length > 0) {
    filtros.push(`Estados: ${campanha.filtros.estados.join(', ')}`)
  }
  if (campanha.filtros.cidade) {
    filtros.push(`Cidade: ${campanha.filtros.cidade}`)
  }
  if (campanha.filtros.data_inicio) {
    filtros.push(`Data início: ${campanha.filtros.data_inicio}`)
  }
  if (campanha.filtros.data_fim) {
    filtros.push(`Data fim: ${campanha.filtros.data_fim}`)
  }
  if (campanha.filtros.dias_vencimento) {
    filtros.push(`Vencimento: ${campanha.filtros.dias_vencimento_filtro} ${campanha.filtros.dias_vencimento} dias`)
  }
  if (campanha.filtros.telefone) {
    filtros.push(`Telefone: ${campanha.filtros.telefone}`)
  }
  if (campanha.filtros.nunca_comprou) {
    filtros.push('Clientes que nunca compraram')
  }
  
  return filtros.length > 0 ? filtros.join(' | ') : 'Sem filtros específicos'
}

const toggleRow = (id) => {
  expandedRow.value = expandedRow.value === id ? null : id
  // Carregar estatísticas ao expandir, se ainda não carregou
  if (expandedRow.value === id && !statisticsCache.value[id]) {
    carregarEstatisticasCliques(id)
  }
}

// Duplicar campanha
const duplicarCampanha = async (campanha) => {
  notificationStore.showNotification('Funcionalidade de duplicação em desenvolvimento', 'info')
}

// Excluir/Cancelar campanha
const excluirCampanha = async (id) => {
  try {
    await campanhaStore.cancelarCampanhaSMS(id)
    notificationStore.showNotification('Campanha cancelada com sucesso', 'success')
    // Recarregar a lista
    await carregarCampanhas()
  } catch (error) {
    notificationStore.showNotification('Erro ao cancelar campanha: ' + error.message, 'error')
  }
}
</script>

<template>
  <div>
    <v-container fluid class="pt-0">
      <div class="d-flex align-center justify-space-between mb-6">
        <h1 class="text-h4">Campanhas SMS</h1>
        <v-btn to="/sms-marketing" prepend-icon="mdi-plus" color="primary">
          Nova Campanha
        </v-btn>
      </div>

      <!-- Filtros -->
      <v-card class="mb-6" variant="outlined">
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="searchQuery"
                label="Buscar por nome ou mensagem"
                prepend-icon="mdi-magnify"
                variant="outlined"
                density="compact"
                clearable
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="selectedStatus"
                :items="[
                  { title: 'Todos', value: '' },
                  { title: 'Enviado', value: 'enviado' },
                  { title: 'Agendado', value: 'agendado' },
                  { title: 'Erro', value: 'erro' }
                ]"
                item-title="title"
                item-value="value"
                label="Status"
                variant="outlined"
                density="compact"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Lista de Campanhas -->
      <v-card variant="outlined">
        <v-table v-if="filteredCampanhas.length > 0" hover>
          <thead>
            <tr>
              <th width="5%"></th>
              <th width="25%">Campanha</th>
              <th width="15%">Data de Envio</th>
              <th width="10%">Status</th>
              <th width="15%">Clientes</th>
              <th width="15%">Taxa de Sucesso</th>
              <th width="15%">Ações</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="campanha in filteredCampanhas" :key="campanha.id">
              <tr @click="toggleRow(campanha.id)" class="cursor-pointer">
                <td>
                  <v-icon :icon="expandedRow === campanha.id ? 'mdi-chevron-down' : 'mdi-chevron-right'"></v-icon>
                </td>
                <td>
                  <div class="font-weight-bold">{{ campanha.nome_campanha }}</div>
                  <div class="text-caption text-grey">{{ campanha.mensagem.substring(0, 50) }}...</div>
                </td>
                <td>{{ formatarData(campanha.data_envio) }}</td>
                <td>
                  <v-chip 
                    :color="campanha.status === 'enviado' ? 'success' : 'warning'"
                    label
                    small
                  >
                    {{ campanha.status }}
                  </v-chip>
                </td>
                <td>
                  <div>{{ campanha.total_clientes }} clientes</div>
                  <div class="text-caption">
                    <v-icon icon="mdi-check-circle" color="success" size="small" class="mr-1"></v-icon>
                    {{ campanha.clientes_sucesso }}
                    <v-icon icon="mdi-alert-circle" color="error" size="small" class="ml-2 mr-1"></v-icon>
                    {{ campanha.clientes_erro }}
                  </div>
                </td>
                <td>
                  <v-progress-linear 
                    :model-value="taxaSucesso(campanha)"
                    :color="taxaSucesso(campanha) >= 95 ? 'success' : taxaSucesso(campanha) >= 80 ? 'warning' : 'error'"
                    height="8"
                    class="mb-1"
                  ></v-progress-linear>
                  <div class="text-caption">{{ taxaSucesso(campanha) }}%</div>
                </td>
                <td>
                  <v-btn
                    icon="mdi-content-copy"
                    variant="text"
                    size="small"
                    @click.stop="duplicarCampanha(campanha)"
                    title="Duplicar campanha"
                  ></v-btn>
                  <v-btn
                    icon="mdi-delete"
                    variant="text"
                    size="small"
                    color="error"
                    @click.stop="excluirCampanha(campanha.id)"
                    title="Excluir campanha"
                  ></v-btn>
                </td>
              </tr>

              <!-- Linha expandida com detalhes -->
              <tr v-if="expandedRow === campanha.id" class="bg-grey-lighten-5">
                <td colspan="7" class="pa-6">
                  <div class="d-flex gap-4">
                    <!-- Seção: Mensagem -->
                    <div class="flex-grow-1">
                      <h4 class="text-subtitle-2 mb-2">Mensagem Completa</h4>
                      <v-card variant="outlined" class="pa-3 bg-grey-lighten-4">
                        <p class="mb-0">{{ campanha.mensagem }}</p>
                      </v-card>
                    </div>

                    <!-- Seção: Filtros -->
                    <div class="flex-grow-1">
                      <h4 class="text-subtitle-2 mb-2">Filtros Aplicados</h4>
                      <v-card variant="outlined" class="pa-3 bg-grey-lighten-4">
                        <p class="text-caption mb-0">{{ descricaoFiltros(campanha) }}</p>
                      </v-card>
                    </div>

                    <!-- Seção: Estatísticas -->
                    <div class="flex-grow-0" style="min-width: 150px;">
                      <h4 class="text-subtitle-2 mb-2">Estatísticas</h4>
                      <div class="text-caption">
                        <div class="mb-2">
                          <v-icon icon="mdi-send" size="small" color="primary"></v-icon>
                          <strong>Total:</strong> {{ campanha.total_clientes }}
                        </div>
                        <div class="mb-2">
                          <v-icon icon="mdi-check-circle" size="small" color="success"></v-icon>
                          <strong>Êxito:</strong> {{ campanha.clientes_sucesso }}
                        </div>
                        <div class="mb-2">
                          <v-icon icon="mdi-close-circle" size="small" color="error"></v-icon>
                          <strong>Erro:</strong> {{ campanha.clientes_erro }}
                        </div>
                        <v-divider class="my-2"></v-divider>
                        <div class="mb-2">
                          <v-icon icon="mdi-link-variant" size="small" color="info"></v-icon>
                          <strong>Cliques:</strong> {{ campanha.clientes_clicaram }}
                        </div>
                        <div>
                          <v-icon icon="mdi-percent" size="small" color="warning"></v-icon>
                          <strong>CTR:</strong> {{ obterTaxaClique(campanha.id, campanha) }}%
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>

        <!-- Mensagem vazia -->
        <v-card-text v-else class="pa-8 text-center">
          <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-inbox-outline</v-icon>
          <p class="text-h6 text-grey-darken-1">Nenhuma campanha encontrada</p>
          <p class="text-body-2 text-grey">Crie sua primeira campanha SMS para começar</p>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.bg-grey-lighten-5 {
  background-color: #fafafa;
}

.bg-grey-lighten-4 {
  background-color: #f5f5f5;
}
</style>
